export const CONTACT_EMAIL = 'info@truss-security.com';

export type BillingRole = 'community' | 'access' | 'deployment' | 'enterprise';
export type CheckoutPlan = 'access' | 'deployment';
export type PlanCtaType = 'none' | 'checkout' | 'contact';

export interface PlanFeature {
  name: string;
  included: boolean;
}

export interface PricingPlan {
  name: string;
  slug: BillingRole;
  tagline: string;
  audience: string;
  feedSupport: string;
  price: number | null;
  priceDisplay?: string;
  interval: 'month' | null;
  features: PlanFeature[];
  ctaType: PlanCtaType;
  ctaLabel?: string;
}

export interface FeatureComparisonRow {
  feature: string;
  community: boolean | string;
  access: boolean | string;
  deployment: boolean | string;
  enterprise: boolean | string;
}

export const FEATURE_COMPARISON = {
  title: 'Compare plans by feature',
  intro:
    'Truss pricing is based on access to capabilities—not event counts or API volume. Choose the tier that matches how you explore, automate, and integrate threat intelligence.',
  footnote:
    'Access and Deployment are billed monthly via Stripe. Enterprise includes all features scaled for larger security teams, MSSPs, MDR operations, and multi-team environments.',
  rows: [
    {
      feature: 'Dashboard access',
      community: true,
      access: true,
      deployment: true,
      enterprise: true,
    },
    {
      feature: 'Discord community',
      community: true,
      access: true,
      deployment: true,
      enterprise: true,
    },
    {
      feature: 'API key + Truss Query Language (TQL)',
      community: false,
      access: true,
      deployment: true,
      enterprise: true,
    },
    {
      feature: 'Truss Agent automation',
      community: false,
      access: true,
      deployment: true,
      enterprise: true,
    },
    {
      feature: 'Truss MCP endpoint (hosted + your MCP stack)',
      community: false,
      access: false,
      deployment: true,
      enterprise: true,
    },
    {
      feature: 'Truss Local MCP Agent (detection rules, IOC push)',
      community: false,
      access: false,
      deployment: true,
      enterprise: true,
    },
    {
      feature: 'Multi-team / MSSP / MDR scale',
      community: false,
      access: false,
      deployment: false,
      enterprise: true,
    },
  ] as FeatureComparisonRow[],
} as const;

const BILLING_ROLE_ORDER: Record<BillingRole, number> = {
  community: 0,
  access: 1,
  deployment: 2,
  enterprise: 3,
};

/** Maps legacy role strings until DB/JWT refresh completes. */
export function normalizeBillingRole(role: string): string {
  if (role === 'user' || role === 'growth') return 'access';
  if (role === 'pro' || role === 'scale' || role === 'contributor') return 'deployment';
  return role;
}

export const PRICING_PLANS: PricingPlan[] = [
  {
    name: 'Community',
    slug: 'community',
    tagline: 'Explore threat intelligence in the dashboard',
    audience:
      'Security teams evaluating Truss before production integrations. Full dashboard access for exploration, contributor discovery, and manual intelligence review.',
    feedSupport:
      'Dashboard only—no API key, no MCP, and no Agent automation. Ideal for pilots and proof-of-concept exploration.',
    price: 0,
    interval: 'month',
    features: [
      { name: 'Full dashboard access', included: true },
      { name: 'Discord community', included: true },
      { name: 'API key + Truss Query Language (TQL)', included: false },
      { name: 'Truss Agent automation', included: false },
      { name: 'Truss MCP endpoint', included: false },
      { name: 'Truss Local MCP Agent', included: false },
    ],
    ctaType: 'none',
    ctaLabel: 'Current plan',
  },
  {
    name: 'Access',
    slug: 'access',
    tagline: 'Automate with API access and the Truss Agent',
    audience:
      'Security teams ready to operationalize Truss. API key unlocks the Truss Query Language (TQL) for programmatic queries and Truss Agent for scheduled delivery into your security stack.',
    feedSupport:
      'API access and Truss Agent automation—query products, run filters, and push intelligence to your tools. No MCP endpoint or Local MCP Agent.',
    price: 200,
    interval: 'month',
    features: [
      { name: 'Everything in Community', included: true },
      { name: 'API key + Truss Query Language (TQL)', included: true },
      { name: 'Truss Agent automation', included: true },
      { name: 'Commercial use', included: true },
      { name: 'Truss MCP endpoint', included: false },
      { name: 'Truss Local MCP Agent', included: false },
    ],
    ctaType: 'checkout',
    ctaLabel: 'Upgrade to Access',
  },
  {
    name: 'Deployment',
    slug: 'deployment',
    tagline: 'Integrate Truss into your AI and LLM operations',
    audience:
      'Teams integrating Truss data into AI/LLM workflows. Includes all Access features plus the Truss MCP endpoint for dynamic, smart queries via your favored LLM and MCP stack.',
    feedSupport:
      'API + MCP Deployments. Query Truss through the hosted MCP endpoint or your own MCP stack. Use the Truss Local MCP Agent to query products, build detection rules, and push IOCs to your security tools—powered by your LLM.',
    price: 500,
    interval: 'month',
    features: [
      { name: 'Everything in Access', included: true },
      { name: 'Truss MCP endpoint (hosted + your MCP stack)', included: true },
      { name: 'Truss Local MCP Agent', included: true },
      { name: 'OAuth consent for MCP Apps (Cursor, Claude, etc.)', included: true },
      { name: 'Priority support', included: true },
    ],
    ctaType: 'checkout',
    ctaLabel: 'Upgrade to Deployment',
  },
  {
    name: 'Enterprise',
    slug: 'enterprise',
    tagline: 'Enterprise-grade delivery at organizational scale',
    audience:
      'Large security teams, MSSPs, MDR operations, and multi-team environments that need all Truss features scaled with dedicated support and custom deployment.',
    feedSupport:
      'All Access and Deployment capabilities at scale—multi-seat deployments, per-client API keys, deep stack integration, and tailored MCP/Agent rollout.',
    price: null,
    priceDisplay: 'Contact Truss',
    interval: null,
    features: [
      { name: 'Everything in Deployment', included: true },
      { name: 'Multi-team / MSSP / MDR scale', included: true },
      { name: 'Per-client API keys for MSSP and MDR deployments', included: true },
      { name: 'Deep integration with your security tooling stack', included: true },
      { name: 'Tailored Truss MCP and Agent deployment', included: true },
      { name: 'Dedicated support, onboarding, and contract pricing', included: true },
    ],
    ctaType: 'contact',
    ctaLabel: 'Contact Truss',
  },
];

export interface ContributorProgramPillar {
  title: string;
  description: string;
}

export const CONTRIBUTOR_PROGRAM = {
  title: 'Contributor Program',
  tagline: 'Power the Decentralized Security Information Ecosystem',
  summary:
    'Contributors are how Truss delivers modern security information at scale. By publishing trusted intelligence to the network, Contributors give Truss Users a curated feed of the sources they rely on—when it matters most in production.',
  pillars: [
    {
      title: 'Contributors publish',
      description:
        'Share security information with the Truss network—reports, indicators, analysis, and context formatted for programmatic use across the ecosystem.',
    },
    {
      title: 'Users subscribe',
      description:
        'Truss Users subscribe to the Contributors they trust and whose information they value, building a personal intelligence pipeline instead of chasing noise.',
    },
    {
      title: 'Production-ready delivery',
      description:
        'Trusted, timely security information flows directly into the workflows, automations, and environments where teams need it—API, agents, and MCP included.',
    },
  ] as ContributorProgramPillar[],
  ctaLabel: 'Become a Contributor',
} as const;

export function planLabelForRole(role: string): string | null {
  const plan = PRICING_PLANS.find((p) => p.slug === normalizeBillingRole(role));
  return plan?.name ?? null;
}

export function isBillingRole(role: string): role is BillingRole {
  return normalizeBillingRole(role) in BILLING_ROLE_ORDER;
}

export function isPaidBillingRole(role: string): boolean {
  const normalized = normalizeBillingRole(role);
  return normalized === 'access' || normalized === 'deployment';
}

export function isCheckoutPlan(plan: string): plan is CheckoutPlan {
  return (
    plan === 'access' ||
    plan === 'deployment' ||
    plan === 'growth' ||
    plan === 'scale' ||
    plan === 'user' ||
    plan === 'pro'
  );
}

/** Normalize checkout plan aliases to canonical access|deployment. */
export function normalizeCheckoutPlan(plan: string): CheckoutPlan | null {
  if (plan === 'access' || plan === 'growth' || plan === 'user') return 'access';
  if (plan === 'deployment' || plan === 'scale' || plan === 'pro') return 'deployment';
  return null;
}

export function planRank(role: string): number {
  const normalized = normalizeBillingRole(role);
  if (isBillingRole(normalized)) {
    return BILLING_ROLE_ORDER[normalized];
  }
  return -1;
}

/** Dashboard accounts that may edit staff-only data such as setup guides. */
export function isStaffRole(role: string): boolean {
  return role === 'truss' || role === 'test';
}

/** Internal/admin roles that bypass plan gates in the dashboard. */
function isInternalBypassRole(role: string): boolean {
  return isStaffRole(role);
}

/** API key, TQL, and Truss Agent require Access or above. */
export function hasApiAccess(role: string): boolean {
  if (isInternalBypassRole(role)) return true;
  return planRank(role) >= planRank('access');
}

/** MCP OAuth and MCP UI require Deployment or above. */
export function hasMcpAccess(role: string): boolean {
  if (isInternalBypassRole(role)) return true;
  const normalized = normalizeBillingRole(role);
  return normalized === 'deployment' || normalized === 'enterprise';
}

export function canUpgradeTo(currentRole: string, targetSlug: BillingRole): boolean {
  const normalized = normalizeBillingRole(currentRole);
  if (!isBillingRole(normalized)) {
    return false;
  }
  return BILLING_ROLE_ORDER[targetSlug] > BILLING_ROLE_ORDER[normalized];
}

export function enterpriseContactMailto(): string {
  const subject = encodeURIComponent('Truss Enterprise Plan Inquiry');
  const body = encodeURIComponent(
    'Hello Truss team,\n\nI am interested in learning more about the Enterprise plan.\n\nOrganization:\nUse case (e.g. large org, MSSP, MDR):\nNumber of users / agents / clients:\nSecurity tools you integrate with:\n'
  );
  return `mailto:${CONTACT_EMAIL}?subject=${subject}&body=${body}`;
}

export function contributorContactMailto(): string {
  const subject = encodeURIComponent('Truss Contributor Program Inquiry');
  const body = encodeURIComponent(
    'Hello Truss team,\n\nI am interested in joining the Contributor Program and publishing security information to the Truss ecosystem.\n\nOrganization:\nArea of expertise:\nTypes of intelligence I publish:\n'
  );
  return `mailto:${CONTACT_EMAIL}?subject=${subject}&body=${body}`;
}

/** Opens the user's default mail client; works more reliably than assigning window.location. */
export function openMailto(href: string): void {
  const anchor = document.createElement('a');
  anchor.href = href;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
}
